import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { get, range, map, reverse, orderBy, find, includes, filter, isEmpty, isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';

import { PRIVILEGES, useAuth } from 'app/auth.ctx';
import { useGetListsQuery } from 'app/apis/lists.api';

export const Lists = {
  AdherenceTypes: 'adherenceTypes',
  AdherenceUpdateTypes: 'adherenceUpdateTypes',
  Committees: 'committees',
  Countries: 'countries',
  InstrumentStatusTypes: 'instrumentStatusTypes',
  InstrumentStatusUpdateTypes: 'instrumentStatusUpdateTypes',
  InstrumentTypes: 'instrumentTypes',
  Languages: 'languages',
  MonitoringTypes: 'monitoringTypes',
  Themes: 'themes',
  Years: 'years',
  Tags: 'tags',
  AdvancedSearchDateType: 'dateType',
  PublishStatus: 'publishStatus',
};

const yearsRange = range(1952, new Date().getFullYear() + 1);
const YearsList = map(reverse(yearsRange), y => ({ id: y, name: `${y}` }));

export const AdvancedSearchDateTypes = [
  { id: 'adoption', nameKey: 'filters.yearOfAdoption' },
  { id: 'inForce', nameKey: 'filters.yearOfInForce' },
  { id: 'abrogation', nameKey: 'filters.yearOfAbrogation' },
  { id: 'lastAmendment', nameKey: 'filters.yearOfLastAmendment' },
  { id: 'monitoring', nameKey: 'filters.monitoringYear' },
];

export const PublishStatusesList = [
  { id: 'published', nameKey: 'filters.published' },
  { id: 'notPublished', nameKey: 'filters.notPublished' },
];

export const InstrumentStatusUpdateTypesIds = {
  FollowUpReport: 9
};

export const AdherentStatusIds = {
  NonAdherence: 7,
  Abstention: 3,
};

export const AdherenceUpdateStatusIds = {
  Adherence: 1,
  AdherenceAsNonMember: 2,
  Abstention: 3,
  AbstentionWithdrawal: 4,
  Statement: 5,
  Withdrawal: 6,
  NonAdherence: 7,
  StatementWithdrawal: 8,
};

const FormListAutocomplete = (props) => {
  const {
    name,
    listName,
    label,
    multiple = false,
    listOfObjects = false,
    checkByValue = true,
    sortKeys = 'sortKey',
    ...restProps
  } = props;

  const params = useParams();
  const { hasPrivilege } = useAuth();
  const { data: lists, isLoading } = useGetListsQuery({ lang: props.lang || params.lang });
  const { t } = useTranslation();


  if (isLoading) {
    return (
      <Skeleton>
        <TextField fullWidth size="small"/>
      </Skeleton>
    )
  }

  let listItems = [];
  if (listName === Lists.Years) {
    listItems = YearsList;
  } else if (listName === Lists.AdvancedSearchDateType) {
    listItems = hasPrivilege(PRIVILEGES.viewMonitoring)
      ? AdvancedSearchDateTypes
      : filter(AdvancedSearchDateTypes, x => x.id !== 'monitoring');
  } else if (listName === Lists.PublishStatus) {
    listItems = PublishStatusesList;
  } else {
    listItems = orderBy(get(lists, listName, []), sortKeys);
  }

  const autocompleteOptions = filter(listItems, e => isUndefined(e.active) || String(e.active) === 'true');

  return (
    <Controller
      name={name}
      render={({ field }) => {

        const autocompleteProps = {
          disableCloseOnSelect: multiple,
          ...restProps,
        };

        if (multiple) {
          if (listOfObjects) {
            autocompleteProps.value = filter(listItems, cv => field.value?.some(e => e.id == cv.id));
            autocompleteProps.onChange = (e, newVal) => {
              let newValToSet = [...newVal];
              field.onChange(map(newValToSet, cv => ({ id: cv.id })))
            };
          } else {
            autocompleteProps.value = filter(listItems, cv => includes(field.value, cv.id));
            autocompleteProps.onChange = (e, newVal) => {
              let newValToSet = [...newVal];
              field.onChange(map(newValToSet, cv => cv.id))
            };
          }
        } else {
          autocompleteProps.value = find(listItems, cv => cv.id == field.value) || null;
          autocompleteProps.onChange = (e, newVal) => {
            field.onChange(get(newVal, 'id', null));
          };
        }

        return (
          <Autocomplete
            fullWidth
            size="small"
            multiple={multiple}
            limitTags={7}
            renderInput={params => (
              <TextField
                {...params}
                label={label || t(listName)}
                placeholder={isEmpty(field.value) ? t('all') : undefined}
              />
            )}
            options={autocompleteOptions}
            getOptionLabel={opt => {
              if (opt?.nameKey) {
                return t(opt.nameKey);
              }
              return get(opt, 'name')
            }}
            {...autocompleteProps}
          />
        )
      }}
    />
  )
};

export default FormListAutocomplete;
